import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  EditPane,
  Field,
  Input,
  MultiSelect,
  SeriesBarChart,
  toast,
  type EditPaneTab,
} from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isoDate } from "../../api/reporting";
import {
  requestTotal,
  ServiceClient,
  type ServicePartDTO,
  type ServiceRequestLineDTO,
} from "../../api/service";

/* ---------------------------------------------------------------- chart --- */

function RequestChart({ sku }: { sku: string }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const service = useMemo(() => new ServiceClient(http), [http]);

  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState<string[]>([String(currentYear)]);

  const chartQuery = useQuery({
    queryKey: ["service", "chart", sku, [...years].sort().join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        years.map(async (year) => ({
          year,
          data: await service
            .requestsForProduct(sku, { from: `${year}-01-01`, to: `${year}-12-31` })
            .catch(() => null),
        })),
      );
      // X axis = every motive seen in any loaded year, ordered by motive id.
      const motives = new Map<number, { id: number; label: string }>();
      for (const result of results) {
        for (const entry of result.data?.motive_service_requests ?? []) {
          if (entry.motive?.id !== undefined && !motives.has(entry.motive.id)) {
            motives.set(entry.motive.id, {
              id: entry.motive.id,
              label: entry.motive.description ?? entry.motive.no ?? String(entry.motive.id),
            });
          }
        }
      }
      const ordered = [...motives.values()].sort((a, b) => a.id - b.id);
      const data = ordered.map((motive) => {
        const row: Record<string, unknown> = { motive: motive.label };
        for (const result of results) {
          row[result.year] = result.data ? requestTotal(result.data, motive.id) : 0;
        }
        return row;
      });
      return { data, years: results.map((result) => result.year).sort() };
    },
    enabled: years.length > 0,
    staleTime: 5 * 60_000,
  });

  const yearOptions = Array.from({ length: 3 }, (_, index) => String(currentYear - index)).map(
    (value) => ({ value, label: value }),
  );

  return (
    <div className="flex flex-col gap-4">
      <Field label={t("services.chart.years", "Years")} htmlFor="chart-years">
        <div className="w-64">
          <MultiSelect id="chart-years" options={yearOptions} value={years} onChange={setYears} />
        </div>
      </Field>
      {chartQuery.data && chartQuery.data.data.length > 0 ? (
        <SeriesBarChart
          data={chartQuery.data.data}
          xKey="motive"
          series={chartQuery.data.years.map((year) => ({ key: year, label: year }))}
          height={360}
        />
      ) : (
        <p className="text-[13px] text-ink-muted">
          {chartQuery.isLoading ? t("common.loading", "Loading…") : t("table.empty", "Nothing to show")}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------- part analysis --- */

function PartAnalysis({ sku }: { sku: string }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const service = useMemo(() => new ServiceClient(http), [http]);

  const now = new Date();
  const defaultFrom = isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const defaultTo = isoDate(new Date(now.getFullYear(), now.getMonth(), 0));
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [applied, setApplied] = useState({ from: defaultFrom, to: defaultTo });

  const { data, isLoading } = useQuery({
    queryKey: ["service", "parts", sku, applied.from, applied.to],
    queryFn: () => service.partsForProduct(sku, applied),
    staleTime: 5 * 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<ServicePartDTO>();
    return [
      c.custom(
        "part",
        (row) => row.part?.no ?? "",
        (row) => (
          <span>
            {row.part?.no}
            <span className="ml-1 text-[11px] text-ink-faint">{row.part?.description1}</span>
          </span>
        ),
        { headerKey: "column.part", priority: 1 },
      ),
      c.number("quantity", (row) => row.quantity ?? 0, { headerKey: "column.quantity", priority: 2 }),
    ];
  }, []);

  return (
    <div className="flex flex-col">
      <div className="mb-4 flex items-end gap-2">
        <Field label={t("analysis.from", "From")} htmlFor="parts-from">
          <Input id="parts-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="h-8 w-40" />
        </Field>
        <Field label={t("analysis.to", "To")} htmlFor="parts-to">
          <Input id="parts-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} className="h-8 w-40" />
        </Field>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            if (!from || !to) {
              toast.error(t("notification.date.required", "Date is required"));
              return;
            }
            setApplied({ from, to });
          }}
        >
          <RefreshCw className="size-4" />
        </Button>
      </div>
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.part?.id ?? row.part?.no ?? "")}
        loading={isLoading}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </div>
  );
}

/* ------------------------------------------------------------- requests --- */

function orderStatusLabel(status: string | null | undefined): string {
  if (status === "I") return "Invoiced";
  if (status === "C") return "Canceled";
  return status ?? "";
}

function RequestLines({ sku }: { sku: string }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const service = useMemo(() => new ServiceClient(http), [http]);

  // Vaadin pinned this to the last four calendar years.
  const currentYear = new Date().getFullYear();
  const period = { from: `${currentYear - 3}-01-01`, to: `${currentYear}-12-31` };

  const { data, isLoading } = useQuery({
    queryKey: ["service", "requests", sku],
    queryFn: () => service.requestLinesForProduct(sku, period),
    staleTime: 5 * 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<ServiceRequestLineDTO>();
    return [
      c.text("order", (row) => String(row.order_no ?? ""), { headerKey: "column.order", priority: 1 }),
      c.text("order_date", (row) => row.order_date ?? "", {
        headerKey: "column.order.date",
        filter: "daterange",
        priority: 3,
      }),
      c.custom(
        "status",
        (row) => orderStatusLabel(row.order_status),
        (row) => (
          <Badge variant={row.order_status === "I" ? "success" : "neutral"} size="sm">
            {orderStatusLabel(row.order_status)}
          </Badge>
        ),
        { headerKey: "column.order.status", filter: "select" },
      ),
      c.custom(
        "part",
        (row) => row.part?.no ?? "",
        (row) => (
          <span>
            {row.part?.no}
            <span className="ml-1 text-[11px] text-ink-faint">{row.part?.description1}</span>
          </span>
        ),
        { headerKey: "column.part", priority: 2 },
      ),
      c.custom(
        "motive",
        (row) => row.motive?.no ?? "",
        (row) => (
          <span>
            {row.motive?.no}
            <span className="ml-1 text-[11px] text-ink-faint">{row.motive?.description}</span>
          </span>
        ),
        { headerKey: "column.motive", filter: "select", priority: 3 },
      ),
      c.number("quantity", (row) => row.quantity ?? 0, { headerKey: "column.quantity" }),
      c.text("invoice_name", (row) => row.invoice_name ?? "", {
        headerKey: "column.invoice.name",
        defaultVisible: false,
      }),
      c.text("client_order_no", (row) => row.client_order_no ?? "", {
        headerKey: "column.client-order-no",
        defaultVisible: false,
      }),
    ];
  }, []);

  return (
    <DataTable
      data={data ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={isLoading}
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}

/* ---------------------------------------------------------------- page --- */

/**
 * One product's customer-service detail: request volumes charted by motive
 * and year, parts consumed over a chosen window, and the raw request lines.
 */
export function ServiceDetailPage({ sku }: { sku: string }) {
  const { t } = useTranslation();

  const tabs: EditPaneTab[] = [
    { id: "chart", label: t("tab.service.product", "Product"), content: <RequestChart sku={sku} /> },
    {
      id: "parts",
      label: t("tab.service.part-analysis", "Part analysis"),
      content: <PartAnalysis sku={sku} />,
    },
    {
      id: "requests",
      flush: true,
      label: t("tab.service.requests", "Requests"),
      content: <RequestLines sku={sku} />,
    },
  ];

  return (
    <>
      <PageHeader
        parent={{ to: "/services", label: t("navigation.item.services", "Services") }}
        title={`${sku}`}
      />
      <EditPane tabs={tabs} urlParam="tab" />
    </>
  );
}
